import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { API_BASE } from '../api/apiClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => {
    const storedToken = localStorage.getItem("nt_token__");
    return storedToken;
  });
  const [user, setUser] = useState(null);

  // Check if token is expired based on stored expiration time
  const isTokenExpired = useCallback(() => {
    const expStr = localStorage.getItem("nt_token_exp__");
    
    if (!expStr) {
      
      return false; // No expiration stored, let backend validate
    }

    const expiry = parseInt(expStr);
    const now = Date.now();
    const isExpired = now >= expiry;

    return isExpired;
  }, []);

  // Clear token and user data
  const clearAuth = useCallback((reason = "unknown") => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("nt_token__");
    localStorage.removeItem("nt_token_exp__");
  }, []);

  const logout = useCallback(async () => {
    const currentToken = localStorage.getItem('nt_token__');
    try {
      if (currentToken) {
        await fetch(`${API_BASE}/api/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${currentToken}` },
        });
      }
    } catch {
      // Clear local credentials even when the API is unavailable.
    } finally {
      clearAuth();
      window.location.assign('/');
    }
  }, [clearAuth]);

   // Schedule automatic cleanup when token expires
  const scheduleTokenCleanup = useCallback((expiresAt) => {
    const timeUntilExpiry = expiresAt - Date.now();

    if (timeUntilExpiry > 0) {
      const timeoutId = setTimeout(() => {
        clearAuth()
      }, timeUntilExpiry);

      return timeoutId;
    } else {
      // Token is already expired
      clearAuth()
    }
  }, [clearAuth]);

  // Store token with expiration time (24 hours from now)
  const storeToken = useCallback((newToken) => {
    if (!newToken) {
      clearAuth()
      return;
    }

    // Validate token format
    if (!newToken.startsWith("v2.local.")) {
      clearAuth()
      return;
    }

    setToken(newToken);
    localStorage.setItem("nt_token__", newToken);

    // Store expiration time (24 hours from now)
    const expiresAt = Date.now() + (24 * 60 * 60 * 1000);
    localStorage.setItem("nt_token_exp__", expiresAt.toString());
    
    // Schedule cleanup
    scheduleTokenCleanup(expiresAt);
  }, [clearAuth, scheduleTokenCleanup]);

  // Check token expiration on mount
  useEffect(() => {
    
    if (token) {
      // Validate token format
      if (!token.startsWith("v2.local.")) {
        clearAuth()
        return;
      }

      // Check if expired
      if (isTokenExpired()) {
        clearAuth()
        return;
      }

      // Schedule cleanup if we have expiration info
      const expStr = localStorage.getItem("nt_token_exp__");
      if (expStr) {
        const timeoutId = scheduleTokenCleanup(parseInt(expStr));
        return () => {
          if (timeoutId) clearTimeout(timeoutId);
        };
      } else {
        // No expiration stored, set it now (token must have been set before our update)
        const expiresAt = Date.now() + (24 * 60 * 60 * 1000);
        localStorage.setItem("nt_token_exp__", expiresAt.toString());
        
      }
    } else {
      
    }
  }, [token, isTokenExpired, clearAuth, scheduleTokenCleanup]);

  // Listen for multi-tab logout or login
  useEffect(() => {
    function handleStorage(e) {
      
      if (e.key === "nt_token__") {
        if (e.newValue) {
          
          setToken(e.newValue);
        } else {
          clearAuth()
        }
      }
    }
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [clearAuth]);

  // Get valid token (returns null if expired)
  const getValidToken = useCallback(() => {
    if (!token) return null;

    if (isTokenExpired()) {
      clearAuth()
      return null;
    }

    return token;
  }, [token, isTokenExpired, clearAuth]);

  return (
    <AuthContext.Provider value={{
      token,
      setToken: storeToken,
      user,
      setUser,
      getValidToken,
      clearAuth,
      logout,
      isTokenExpired
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
